// A filled price band drawn on the chart's own canvas.
//
// lightweight-charts v4 has lines but no rectangle, so the risk block, the
// reward block and a confluence zone all have to be a custom series primitive.
// This is that primitive: an axis-aligned box spanning the full visible width
// between two prices, drawn UNDER the candles so it never hides price.
//
// Kept deliberately small -- one shape, no interactivity. Anything a user can
// tap belongs in DOM, where it can be focused, labelled and read aloud.

class BoxPaneRenderer {
  constructor(source) {
    this._source = source;
  }

  draw() { /* nothing on the foreground: price stays readable */ }

  drawBackground(target) {
    const source = this._source;
    if (!source._visible) return;
    target.useBitmapCoordinateSpace((scope) => {
      const series = source._series;
      const y1 = series.priceToCoordinate(source._top);
      const y2 = series.priceToCoordinate(source._bottom);
      if (y1 === null || y2 === null) return;
      const ctx = scope.context;
      const ratio = scope.verticalPixelRatio;
      const top = Math.min(y1, y2) * ratio;
      const height = Math.abs(y2 - y1) * ratio;
      ctx.save();
      ctx.fillStyle = source._fill;
      ctx.fillRect(0, top, scope.bitmapSize.width, Math.max(height, 1));
      if (source._border) {
        ctx.strokeStyle = source._border;
        ctx.lineWidth = Math.max(1, Math.floor(ratio));
        ctx.beginPath();
        ctx.moveTo(0, top);
        ctx.lineTo(scope.bitmapSize.width, top);
        ctx.moveTo(0, top + height);
        ctx.lineTo(scope.bitmapSize.width, top + height);
        ctx.stroke();
      }
      ctx.restore();
    });
  }
}

class BoxPaneView {
  constructor(source) {
    this._source = source;
    this._renderer = new BoxPaneRenderer(source);
  }
  zOrder() { return "bottom"; }
  update() {}
  renderer() { return this._renderer; }
}

export class PriceBox {
  /**
   * @param {number} top     upper price of the band
   * @param {number} bottom  lower price of the band
   * @param {string} fill    rgba fill
   * @param {string} [border] optional edge colour
   */
  constructor(top, bottom, fill, border) {
    this._top = Math.max(top, bottom);
    this._bottom = Math.min(top, bottom);
    this._fill = fill;
    this._border = border || null;
    this._visible = true;
    this._paneViews = [new BoxPaneView(this)];
  }

  attached({ series }) { this._series = series; }
  detached() { this._series = null; }
  paneViews() { return this._paneViews; }
  updateAllViews() { this._paneViews.forEach((v) => v.update()); }

  // The band must never be clipped by autoscale: a stop you cannot see is the
  // one number a trader most needs on screen.
  autoscaleInfo() {
    if (!this._visible) return null;
    return { priceRange: { minValue: this._bottom, maxValue: this._top } };
  }

  set(top, bottom) {
    this._top = Math.max(top, bottom);
    this._bottom = Math.min(top, bottom);
    this.updateAllViews();
  }

  setVisible(visible) {
    this._visible = !!visible;
    this.updateAllViews();
  }
}
